// ============================================
// ARCHIVES DES DOSSIERS
// ============================================
// Demande de Nathan du 19/10/2026 : « un bouton archiver et un onglet
// archives ». Dans le SAV, l'état d'archive est le statut « Fermé » (CLOSED) :
// rien n'est supprimé, le client garde ses dossiers dans son espace, et un
// e-mail du client sur un dossier fermé le rouvre (REOPENED).
//   - Archiver = passer en « Fermé » (PUT /admin/tickets/:id). Case « Sans
//     prévenir le client » : `sansNotification`, le serveur n'envoie pas
//     l'avis de changement de statut pour cette mise à jour.
//   - Désarchiver = repasser en « Réouvert », comme « Réouvrir ».
//   - Onglet Archives : GET /admin/tickets?status=CLOSED, du plus récemment
//     fermé au plus ancien (dernière mise à jour : pas de date de clôture en
//     base), recherche du serveur, période de fermeture (updatedFrom).
// Helpers purs, sans React.

import { TicketStatus, type Ticket, type UpdateTicketPayload } from '../../types';
import { LIMITE_MAX, PAR_PAGE, type FiltresApiDossiers } from './listeDossiers';
import { decalerJour, jourParis } from '../../shared/dates';

/** Identifiant de l'écran dans la navigation (menu latéral, tiroir mobile). */
export const VUE_ARCHIVES = 'archives';

export type PeriodeArchives = 'tout' | '30j' | '6m';

export const PERIODES_ARCHIVES: Array<{ value: PeriodeArchives; label: string }> = [
  { value: 'tout', label: 'Toutes les dates' },
  { value: '30j', label: '30 derniers jours' },
  { value: '6m', label: '6 derniers mois' },
];

/**
 * Premier jour de la période de fermeture, jour de Paris AAAA-MM-JJ (le
 * serveur le lit à minuit, heure de Paris) ; `undefined` pour « Toutes les dates ».
 */
export function debutPeriode(periode: PeriodeArchives, maintenant: Date = new Date()): string | undefined {
  if (periode === 'tout') return undefined;
  const aujourdhui = jourParis(maintenant);
  if (periode === '30j') return decalerJour(aujourdhui, -30);
  const [a, m, j] = aujourdhui.split('-').map(Number);
  return new Date(Date.UTC(a, m - 1 - 6, j)).toISOString().slice(0, 10);
}

export interface EtatArchives {
  recherche: string;
  periode: PeriodeArchives;
  page: number;
  parPage: number;
}

export const ETAT_ARCHIVES_PAR_DEFAUT: EtatArchives = { recherche: '', periode: 'tout', page: 1, parPage: PAR_PAGE };

/** État de l'écran → filtres de GET /admin/tickets (toujours status=CLOSED). */
export function construireFiltresArchives(etat: EtatArchives, maintenant: Date = new Date()): FiltresApiDossiers {
  const filtres: FiltresApiDossiers = {
    status: TicketStatus.CLOSED,
    page: Math.max(1, Math.floor(etat.page || 1)),
    limit: Math.min(LIMITE_MAX, Math.max(1, Math.floor(etat.parPage || PAR_PAGE))),
    sortBy: 'updatedAt',
    sortOrder: 'desc',
  };
  const recherche = etat.recherche.trim();
  if (recherche) filtres.search = recherche;
  const debut = debutPeriode(etat.periode, maintenant);
  if (debut) filtres.updatedFrom = debut;
  return filtres;
}

/** Date affichée dans la colonne « Fermé le » : la date de clôture si le serveur la donne, sinon la dernière mise à jour. */
export function dateFermeture(dossier: Pick<Ticket, 'updatedAt'> & { closedAt?: string | Date | null }): string | Date {
  return dossier.closedAt || dossier.updatedAt;
}

/** Corps du PUT d'archivage. */
export function chargeArchivage(sansPrevenir: boolean): UpdateTicketPayload {
  return sansPrevenir ? { status: TicketStatus.CLOSED, sansNotification: true } : { status: TicketStatus.CLOSED };
}

/** Corps du PUT de désarchivage : comme « Réouvrir ». */
export const CHARGE_DESARCHIVAGE: UpdateTicketPayload = { status: TicketStatus.REOPENED };

export const libelleSansPrevenir = (nombre: number): string =>
  nombre > 1 ? 'Sans prévenir les clients' : 'Sans prévenir le client';

/** Texte de la fenêtre de confirmation de l'archivage (un dossier ou plusieurs). */
export function texteConfirmationArchivage(nombre: number): string {
  if (nombre > 1) {
    return `Les ${nombre} dossiers passeront en Fermé et rejoindront les Archives. `
      + 'Chaque client sera prévenu par e-mail que son dossier est clôturé '
      + `(sauf si vous cochez « ${libelleSansPrevenir(nombre)} »). Il pourra le rouvrir en écrivant.`;
  }
  return 'Le dossier passera en Fermé et rejoindra les Archives. '
    + 'Le client sera prévenu par e-mail que son dossier est clôturé '
    + `(sauf si vous cochez « ${libelleSansPrevenir(1)} »). Il pourra le rouvrir en écrivant.`;
}

export function texteConfirmationDesarchivage(nombre: number): string {
  return nombre > 1
    ? `Les ${nombre} dossiers repasseront en « Réouvert » et reviendront dans la liste des dossiers à traiter.`
    : 'Le dossier repassera en « Réouvert » et reviendra dans la liste des dossiers à traiter.';
}

/** Phrase affichée sur la fiche après l'archivage. */
export function avisArchivage(sansPrevenir: boolean): string {
  return sansPrevenir
    ? 'Dossier archivé : il est passé en Fermé. Aucun avis n’a été envoyé au client.'
    : 'Dossier archivé : il est passé en Fermé et se trouve dans les Archives.';
}

export const AVIS_DESARCHIVAGE = 'Dossier désarchivé : il est repassé en « Réouvert ».';
